import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { bauvorhabenApi, adminApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import type {
  Bauvorhaben, BauvorhabenContact, BauvorhabenContactRole,
  BauvorhabenStatus, Contact, User, GewerkStatus, AccordionPayload,
} from "@/types";
import {
  ArrowLeft, Pencil, Trash2, Loader2, AlertCircle, X, CheckCircle2,
  MapPin, CalendarRange, User as UserIcon, HardHat, Building2, Save,
  BadgeEuro, ListChecks,
} from "lucide-react";
import { ContactPicker } from "@/components/ContactPicker";
import { GewerkAccordion } from "@/components/GewerkAccordion";

// Reine DATE-Spalten (YYYY-MM-DD) direkt als dd.MM.yyyy rendern —
// kein new Date(), damit keine Zeitzone ins Spiel kommt.
function formatDate(dateStr?: string | null): string {
  if (!dateStr) return "";
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dateStr;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

const statusLabels: Record<BauvorhabenStatus, string> = {
  geplant: "Geplant",
  aktiv: "Aktiv",
  abgeschlossen: "Abgeschlossen",
  abgebrochen: "Abgebrochen",
};

const statusBadgeClasses: Record<BauvorhabenStatus, string> = {
  geplant: "bg-gray-100 text-gray-800",
  aktiv: "bg-green-100 text-green-800",
  abgeschlossen: "bg-blue-100 text-blue-800",
  abgebrochen: "bg-red-100 text-red-800",
};

interface SectionConfig {
  role: BauvorhabenContactRole;
  title: string;
  emptyText: string;
}

const sections: SectionConfig[] = [
  { role: 'bauherr', title: 'Bauherren', emptyText: 'Noch keine Bauherren zugeordnet.' },
  { role: 'sonstiges', title: 'Sonstige Kontakte', emptyText: 'Noch keine weiteren Kontakte zugeordnet.' },
];

function displayName(contact: BauvorhabenContact): string {
  return (
    contact.display_name ||
    [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
    contact.company_name ||
    contact.email_address ||
    'Unbenannter Kontakt'
  );
}

export function BauvorhabenDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isApprover } = useAuth();
  const isNew = id === 'new';

  const [bauvorhaben, setBauvorhaben] = useState<Bauvorhaben | null>(null);
  const [contacts, setContacts] = useState<BauvorhabenContact[]>([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isEditing, setIsEditing] = useState(isNew);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState<User[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    project_number: '',
    street: '',
    postal_code: '',
    city: '',
    status: 'geplant' as BauvorhabenStatus,
    start_date: '',
    end_date: '',
    bauleiter_user_id: '',
    notes: '',
  });

  // Vertriebler-Auswahl im Formular: {id, name} oder null (kein Vertriebler)
  const [vertriebler, setVertriebler] = useState<{ id: number; name: string } | null>(null);

  // Gewerk-Checkliste (Akkordeon) — gesamter aktiver Katalog
  const [accordion, setAccordion] = useState<AccordionPayload | null>(null);

  const [associationError, setAssociationError] = useState('');
  // Positive Rückmeldung (z. B. erzeugte Adress-Anträge für Bauherren)
  const [associationInfo, setAssociationInfo] = useState('');

  const loadDetail = useCallback(() => {
    if (isNew) return;
    setIsLoading(true);
    bauvorhabenApi
      .getById(Number(id))
      .then((response) => {
        setBauvorhaben(response.data.data.bauvorhaben);
        setContacts(response.data.data.contacts);
        setFormData({
          name: response.data.data.bauvorhaben.name ?? '',
          project_number: response.data.data.bauvorhaben.project_number ?? '',
          street: response.data.data.bauvorhaben.street ?? '',
          postal_code: response.data.data.bauvorhaben.postal_code ?? '',
          city: response.data.data.bauvorhaben.city ?? '',
          status: response.data.data.bauvorhaben.status,
          start_date: response.data.data.bauvorhaben.start_date ?? '',
          end_date: response.data.data.bauvorhaben.end_date ?? '',
          bauleiter_user_id: response.data.data.bauvorhaben.bauleiter_user_id
            ? String(response.data.data.bauvorhaben.bauleiter_user_id)
            : '',
          notes: response.data.data.bauvorhaben.notes ?? '',
        });
        setVertriebler(
          response.data.data.bauvorhaben.vertriebler_contact_id
            ? {
                id: response.data.data.bauvorhaben.vertriebler_contact_id,
                name: response.data.data.bauvorhaben.vertriebler_name || 'Kontakt',
              }
            : null
        );
      })
      .catch(() => setError('Bauvorhaben nicht gefunden'))
      .finally(() => setIsLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // Gewerk-Checkliste laden (nur im Detail-Modus)
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    bauvorhabenApi
      .getGewerke(Number(id))
      .then((response) => {
        if (!cancelled) setAccordion(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setAccordion(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  // Benutzerliste für den Bauleiter-Select — nur nötig, wenn editiert wird
  useEffect(() => {
    if (!isEditing || !isApprover || users.length > 0) return;
    adminApi
      .getUsers()
      .then((response) => setUsers(response.data.data))
      .catch(() => setUsers([]));
  }, [isEditing, isApprover, users.length]);

  const handleSave = () => {
    setSaveError('');
    if (!formData.name.trim()) {
      setSaveError('Bitte einen Namen angeben');
      return;
    }
    setIsSaving(true);
    const payload = {
      name: formData.name.trim(),
      project_number: formData.project_number.trim() || null,
      street: formData.street.trim() || null,
      postal_code: formData.postal_code.trim() || null,
      city: formData.city.trim() || null,
      status: formData.status,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      bauleiter_user_id: formData.bauleiter_user_id ? Number(formData.bauleiter_user_id) : null,
      vertriebler_contact_id: vertriebler?.id ?? null,
      notes: formData.notes.trim() || null,
    };
    const request = isNew
      ? bauvorhabenApi.create(payload)
      : bauvorhabenApi.update(Number(id), payload);

    request
      .then((response) => {
        if (isNew) {
          navigate(`/bauvorhaben/${(response.data.data as { id: number }).id}`);
        } else {
          const created = (response.data.data as { address_proposals_created?: number }).address_proposals_created;
          if (created && created > 0) {
            setAssociationInfo(
              `Für ${created} Bauherr(en) wurde ein Antrag zur Übernahme der Bauvorhaben-Adresse erstellt — er muss noch genehmigt werden.`
            );
          }
          setIsEditing(false);
          loadDetail();
        }
      })
      .catch((err) => {
        setSaveError(err.response?.data?.error || 'Fehler beim Speichern');
      })
      .finally(() => setIsSaving(false));
  };

  const handleDelete = () => {
    if (!window.confirm('Möchten Sie dieses Bauvorhaben wirklich löschen?')) return;
    bauvorhabenApi
      .remove(Number(id))
      .then(() => navigate('/bauvorhaben'))
      .catch((err) => setError(err.response?.data?.error || 'Fehler beim Löschen'));
  };

  const handleAddContact = (role: BauvorhabenContactRole, contact: Contact) => {
    if (isNew) return;
    setAssociationError('');
    setAssociationInfo('');
    bauvorhabenApi
      .addContact(Number(id), {
        contact_id: contact.id,
        role,
      })
      .then((res) => {
        const created = res.data.data.address_proposals_created;
        if (created && created > 0) {
          setAssociationInfo(
            `Für ${created} Bauherr(en) wurde ein Antrag zur Übernahme der Bauvorhaben-Adresse erstellt — er muss noch genehmigt werden.`
          );
        }
        loadDetail();
      })
      .catch((err) => {
        setAssociationError(err.response?.data?.error || 'Fehler beim Zuordnen');
      });
  };

  const handleRemoveContact = (linkId: number) => {
    if (isNew) return;
    setAssociationError('');
    bauvorhabenApi
      .removeContact(Number(id), linkId)
      .then(loadDetail)
      .catch((err) => {
        setAssociationError(err.response?.data?.error || 'Fehler beim Entfernen');
      });
  };

  // Gewerk-Status setzen: optimistisch im Akkordeon-State, Reload bei Fehler
  const handleGewerkStatusChange = async (gewerkId: number, status: GewerkStatus) => {
    if (isNew || !accordion) return;
    const previous = accordion;
    const updated: AccordionPayload = {
      ...accordion,
      phases: accordion.phases.map((phase) => ({
        ...phase,
        gewerke: phase.gewerke.map((gewerk) =>
          gewerk.id === gewerkId ? { ...gewerk, status } : gewerk
        ),
      })),
    };
    setAccordion(updated);
    try {
      await bauvorhabenApi.updateGewerkStatus(Number(id), gewerkId, status);
    } catch (err) {
      setAccordion(previous);
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Aktualisieren des Gewerk-Status');
    }
  };

  const handleGewerkAddContact = async (gewerkId: number, contact: Contact) => {
    if (isNew) return;
    setAssociationError('');
    try {
      const res = await bauvorhabenApi.addGewerkContact(Number(id), gewerkId, contact.id);
      const linkId = res.data.data.link_id;
      setAccordion((prev) => prev && ({
        ...prev,
        phases: prev.phases.map((phase) => ({
          ...phase,
          gewerke: phase.gewerke.map((gewerk) =>
            gewerk.id === gewerkId
              ? {
                  ...gewerk,
                  contacts: [
                    ...gewerk.contacts,
                    {
                      link_id: linkId,
                      contact_id: contact.id,
                      display_name: contact.display_name,
                      first_name: contact.first_name,
                      last_name: contact.last_name,
                      company_name: contact.company_name,
                      email_address: contact.email_address,
                      business_phone: contact.business_phone,
                    },
                  ],
                }
              : gewerk
          ),
        })),
      }));
    } catch (err) {
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Zuordnen');
    }
  };

  const handleGewerkRemoveContact = async (gewerkId: number, linkId: number) => {
    if (isNew) return;
    setAssociationError('');
    try {
      await bauvorhabenApi.removeGewerkContact(Number(id), gewerkId, linkId);
      setAccordion((prev) => prev && ({
        ...prev,
        phases: prev.phases.map((phase) => ({
          ...phase,
          gewerke: phase.gewerke.map((gewerk) =>
            gewerk.id === gewerkId
              ? { ...gewerk, contacts: gewerk.contacts.filter((c) => c.link_id !== linkId) }
              : gewerk
          ),
        })),
      }));
    } catch (err) {
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Entfernen');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error && !bauvorhaben) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="card p-8 text-center">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-gray-700 mb-4">{error}</p>
          <Link to="/bauvorhaben" className="btn btn-secondary inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Übersicht
          </Link>
        </div>
      </div>
    );
  }

  const addressParts = [formData.street, formData.postal_code, formData.city].filter(Boolean).join(' ');

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <Link to="/bauvorhaben" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="h-4 w-4" />
          Bauvorhaben
        </Link>
        {!isNew && isApprover && !isEditing && (
          <div className="flex gap-2">
            <button onClick={() => setIsEditing(true)} className="btn btn-secondary inline-flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Bearbeiten
            </button>
            <button onClick={handleDelete} className="btn btn-danger inline-flex items-center gap-2">
              <Trash2 className="h-4 w-4" />
              Löschen
            </button>
          </div>
        )}
      </div>

      {/* Stammdaten */}
      <div className="card p-6 mb-6">
        {isEditing ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {isNew ? 'Neues Bauvorhaben' : 'Bauvorhaben bearbeiten'}
            </h2>
            {saveError && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {saveError}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="label">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Projekt-Nr.</label>
                <input
                  type="text"
                  value={formData.project_number}
                  onChange={(e) => setFormData({ ...formData, project_number: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as BauvorhabenStatus })}
                  className="input"
                >
                  {(Object.keys(statusLabels) as BauvorhabenStatus[]).map((status) => (
                    <option key={status} value={status}>
                      {statusLabels[status]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="label">Straße</label>
                <input
                  type="text"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">PLZ</label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Ort</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Beginn</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Ende</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Bauleiter</label>
                <select
                  value={formData.bauleiter_user_id}
                  onChange={(e) => setFormData({ ...formData, bauleiter_user_id: e.target.value })}
                  className="input"
                >
                  <option value="">— Kein Bauleiter —</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="label">Vertriebler</label>
                {vertriebler ? (
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/contacts/${vertriebler.id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-800 hover:text-primary-700"
                    >
                      <BadgeEuro className="h-4 w-4 text-gray-400" />
                      {vertriebler.name}
                    </Link>
                    <button
                      type="button"
                      onClick={() => setVertriebler(null)}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      title="Vertriebler entfernen"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <ContactPicker
                    onSelect={(contact) =>
                      setVertriebler({
                        id: contact.id,
                        name: contact.display_name || [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.company_name || 'Kontakt',
                      })
                    }
                    placeholder="Vertriebler suchen…"
                  />
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="label">Notizen</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="input"
                />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button type="submit" disabled={isSaving} className="btn btn-primary inline-flex items-center gap-2">
                <Save className="h-4 w-4" />
                {isSaving ? 'Speichern…' : 'Speichern'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isNew) {
                    navigate('/bauvorhaben');
                  } else {
                    setIsEditing(false);
                    loadDetail();
                  }
                }}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{bauvorhaben?.name}</h1>
                {bauvorhaben?.project_number && (
                  <p className="text-sm text-gray-500">Projekt-Nr. {bauvorhaben.project_number}</p>
                )}
              </div>
              {bauvorhaben && (
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClasses[bauvorhaben.status]}`}>
                  {statusLabels[bauvorhaben.status]}
                </span>
              )}
            </div>
            <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <div>
                  <dt className="text-gray-500">Adresse</dt>
                  <dd className="text-gray-900">{addressParts || '—'}</dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <UserIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <div>
                  <dt className="text-gray-500">Bauleiter</dt>
                  <dd className="text-gray-900">
                    {bauvorhaben?.bauleiter_name
                      ? bauvorhaben.bauleiter_contact_id
                        ? (
                          <Link
                            to={`/contacts/${bauvorhaben.bauleiter_contact_id}`}
                            className="text-primary-600 hover:text-primary-700"
                          >
                            {bauvorhaben.bauleiter_name}
                          </Link>
                        )
                        : bauvorhaben.bauleiter_name
                      : '—'}
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CalendarRange className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <div>
                  <dt className="text-gray-500">Zeitraum</dt>
                  <dd className="text-gray-900">
                    {(bauvorhaben?.start_date || bauvorhaben?.end_date)
                      ? [formatDate(bauvorhaben?.start_date), formatDate(bauvorhaben?.end_date)]
                          .filter(Boolean)
                          .join(' – ') || '—'
                      : '—'}
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <BadgeEuro className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <div>
                  <dt className="text-gray-500">Vertriebler</dt>
                  <dd className="text-gray-900">
                    {bauvorhaben?.vertriebler_name
                      ? bauvorhaben.vertriebler_contact_id
                        ? (
                          <Link
                            to={`/contacts/${bauvorhaben.vertriebler_contact_id}`}
                            className="text-primary-600 hover:text-primary-700"
                          >
                            {bauvorhaben.vertriebler_name}
                          </Link>
                        )
                        : bauvorhaben.vertriebler_name
                      : '—'}
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <HardHat className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <div>
                  <dt className="text-gray-500">Kontakte</dt>
                  <dd className="text-gray-900">
                    {contacts.filter((c) => c.role === 'bauherr').length} Bauherren
                  </dd>
                </div>
              </div>
              {bauvorhaben?.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Notizen</dt>
                  <dd className="whitespace-pre-wrap text-gray-900">{bauvorhaben.notes}</dd>
                </div>
              )}
            </dl>
          </>
        )}
      </div>

      {/* Zuordnungen (nur im Detail-Modus, nicht beim Anlegen) */}
      {!isNew && (
        <div className="space-y-6">
          {associationError && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {associationError}
            </div>
          )}
          {associationInfo && (
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {associationInfo}
            </div>
          )}
          {sections.map((section) => {
            const sectionContacts = contacts.filter((c) => c.role === section.role);
            return (
              <div key={section.role} className="card p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{section.title}</h2>
                {sectionContacts.length === 0 ? (
                  <p className="text-sm text-gray-500 mb-4">{section.emptyText}</p>
                ) : (
                  <ul className="mb-4 divide-y divide-gray-100">
                    {sectionContacts.map((contact) => (
                      <li key={contact.link_id} className="flex items-center justify-between gap-3 py-2">
                        <Link
                          to={`/contacts/${contact.contact_id}`}
                          className="min-w-0 flex-1 hover:text-primary-700"
                        >
                          <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                            {displayName(contact)}
                          </span>
                          {(contact.email_address || contact.business_phone) && (
                            <span className="ml-6 block truncate text-xs text-gray-500">
                              {[contact.email_address, contact.business_phone].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </Link>
                        {isApprover && (
                          <button
                            onClick={() => handleRemoveContact(contact.link_id)}
                            className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                            title="Zuordnung entfernen"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {isApprover && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <ContactPicker
                      onSelect={(contact) => handleAddContact(section.role, contact)}
                      placeholder={section.role === 'bauherr' ? 'Bauherr suchen…' : 'Kontakt suchen…'}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {/* Gewerke — Bauablauf-Checkliste (gesamter aktiver Katalog) */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-gray-400" />
              Gewerke — Bauablauf-Checkliste
            </h2>
            {accordion ? (
              <GewerkAccordion
                payload={accordion}
                isApprover={isApprover}
                onStatusChange={handleGewerkStatusChange}
                onAddContact={handleGewerkAddContact}
                onRemoveContact={handleGewerkRemoveContact}
              />
            ) : (
              <p className="text-sm text-gray-500">Gewerke konnten nicht geladen werden.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}